/**
 * Auto-saves Trix editor content to the server as the user types.
 *
 * This controller should be placed directly on the rich_text_area element.
 * It will automatically save drafts after a configurable debounce period and restore
 * them when the page loads. The status indicator is automatically created and positioned
 * in the bottom right corner of the editor.
 *
 * Example:
 *   = form.rich_text_area :nu_content,
 *       data: { controller: "trix--autosave",
 *               "trix--autosave-form-key-value": "conversation_42_message" }
 */
import { Controller } from "@hotwired/stimulus";
import { debounce } from "lodash-es";

export default class extends Controller {
  static values = {
    formKey: String,
    debounceMs: { type: Number, default: 2000 },
  };

  connect() {
    this.debouncedSave = debounce(this.saveDraft.bind(this), this.debounceMsValue);
    this.editor = this.element;
    this.boundHandleChange = this.handleChange.bind(this);
    this.boundHandleSubmitEnd = this.handleSubmitEnd.bind(this);
    this.boundInitializeDrafts = this.initializeDrafts.bind(this);

    this.editor.addEventListener("trix-status-bar-initialize", () => this.boundInitializeDrafts(), {
      once: true,
    });

    this.editor.addEventListener("trix-change", this.boundHandleChange);

    this.form = this.element.closest("form");
    if (this.form) {
      this.form.addEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
    }
  }

  disconnect() {
    if (this.editor) {
      this.editor.removeEventListener("trix-change", this.boundHandleChange);
    }
    if (this.form) {
      this.form.removeEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
    }
    if (this.statusElement) {
      this.statusElement.remove();
    }
    if (this.debouncedSave?.cancel) {
      this.debouncedSave.cancel();
    }
  }

  initializeDrafts() {
    this.createStatusElement();
    this.loadDraft();
  }

  createStatusElement() {
    const statusBar = this.editor.parentElement.querySelector(".trix-editor-status-bar");
    this.statusElement = document.createElement("span");
    this.statusElement.className = "autosave-status";
    statusBar.appendChild(this.statusElement);
  }

  handleChange() {
    this.updateStatus("saving");
    this.debouncedSave();
  }

  handleSubmitEnd(event) {
    if (event.detail.success) {
      this.clearDraft();
    }
  }

  async loadDraft() {
    if (!this.formKeyValue) return;

    try {
      const response = await fetch(`/rich_text_drafts/${encodeURIComponent(this.formKeyValue)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.content) {
          this.savedDraftContent = data.content;

          if (this.isEditorEmpty()) {
            this.applyDraft();
          } else if (this.editorContentDiffersFromDraft()) {
            this.showLoadDraftLink();
          }
        }
      }
    } catch (error) {
      console.error("Failed to load draft:", error);
    }
  }

  applyDraft() {
    if (this.savedDraftContent) {
      this.editor.editor.loadHTML(this.savedDraftContent);
      this.updateStatus("restored");
    }
  }

  showLoadDraftLink() {
    if (!this.statusElement) return;

    const link = document.createElement("a");
    link.href = "#";
    link.textContent = "Load saved draft";
    link.className = "autosave-load-draft-link";
    link.addEventListener("click", (e) => {
      e.preventDefault();
      this.applyDraft();
    });

    this.statusElement.textContent = "";
    this.statusElement.appendChild(link);
  }

  async saveDraft() {
    if (!this.formKeyValue) return;

    const content = this.editor.innerHTML;

    try {
      const response = await fetch("/rich_text_drafts/upsert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({
          nu_rich_text_draft: {
            nu_form_key: this.formKeyValue,
            nu_content: content,
          },
        }),
      });

      if (response.ok) {
        this.updateStatus("saved");
      } else {
        this.updateStatus("error");
      }
    } catch (error) {
      console.error("Failed to save draft:", error);
      this.updateStatus("error");
    }
  }

  async clearDraft() {
    if (!this.formKeyValue) return;

    try {
      await fetch(`/rich_text_drafts/${encodeURIComponent(this.formKeyValue)}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
      });
    } catch (error) {
      console.error("Failed to clear draft:", error);
    }
  }

  updateStatus(status) {
    if (!this.statusElement) return;

    const messages = {
      saving: "Saving draft...",
      saved: "Draft saved",
      restored: "Draft restored",
      error: "Save failed",
    };

    this.statusElement.textContent = messages[status] || "";

    if (status === "saved" || status === "restored") {
      setTimeout(() => {
        if (this.statusElement) {
          this.statusElement.textContent = "";
        }
      }, 2000);
    }
  }

  isEditorEmpty() {
    const doc = this.editor.editor.getDocument();
    const text = doc.toString().trim();
    return text === "" || text === "\n";
  }

  editorContentDiffersFromDraft() {
    return this.editor.innerHTML !== this.savedDraftContent;
  }
}
